// Firestore analytics service
// Reads/writes pre-computed analytics and handles session tracking

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import type { EventAnalytics } from '../types/analytics';

// ============================================
// EVENT ANALYTICS (pre-computed by Cloud Functions)
// ============================================

export async function getEventAnalytics(eventId: string): Promise<EventAnalytics | null> {
  const docRef = doc(db, 'eventAnalytics', eventId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return null;
  return snap.data() as EventAnalytics;
}

export async function getDjEventAnalytics(djId: string): Promise<EventAnalytics[]> {
  const q = query(collection(db, 'eventAnalytics'), where('djId', '==', djId));
  const snap = await getDocs(q);
  return snap.docs.map(d => d.data() as EventAnalytics);
}

export async function refreshEventAnalytics(eventId: string): Promise<void> {
  const callable = httpsCallable<{ eventId: string }, unknown>(functions, 'computeAnalytics');
  await callable({ eventId });
}

// ============================================
// SESSION TRACKING
// ============================================

interface SessionDoc {
  attendeeId: string;
  eventId: string;
  startedAt: Timestamp;
  endedAt?: Timestamp;
  deviceType: string;
  userAgent: string;
}

function detectDeviceType(): string {
  const ua = navigator.userAgent;
  if (/iPad|tablet/i.test(ua)) return 'tablet';
  if (/iPhone|Android.*Mobile|Mobile/i.test(ua)) return 'mobile';
  return 'desktop';
}

export async function startSession(eventId: string, attendeeId: string): Promise<string> {
  const sessionsRef = collection(db, 'events', eventId, 'sessions');
  const sessionDoc: SessionDoc = {
    attendeeId,
    eventId,
    startedAt: Timestamp.now(),
    deviceType: detectDeviceType(),
    userAgent: navigator.userAgent.substring(0, 200),
  };
  const docRef = await addDoc(sessionsRef, sessionDoc);
  return docRef.id;
}

export async function endSession(eventId: string, sessionId: string): Promise<void> {
  const sessionRef = doc(db, 'events', eventId, 'sessions', sessionId);
  await updateDoc(sessionRef, { endedAt: Timestamp.now() });
}

// Get count of unique visitors (unique attendeeIds in sessions subcollection)
export async function getUniqueVisitorCount(eventId: string): Promise<number> {
  const sessionsRef = collection(db, 'events', eventId, 'sessions');
  const snap = await getDocs(sessionsRef);
  const uniqueIds = new Set(snap.docs.map(d => d.data().attendeeId));
  return uniqueIds.size;
}
